import React, { useEffect, useState, useMemo } from 'react';
import { FaCheckCircle } from "react-icons/fa";
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import SummaryApi from '../common';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import { IoIosCloseCircle } from "react-icons/io";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { GrStatusGood } from "react-icons/gr";

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js / InterestedStudents.js
// primary → header text, form labels, primary buttons
// actionBlue → Edit / secondary action buttons
// actionYellow → View More (matches "Not Answered" tone)
// actionGreen → success / positive / lead-closing actions
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

// Columns to stick to the right side
const STICKY_ACCESSORS = ['edit', 'view', 'close'];

// Status chip colors (semantic palette)
const STATUS_CHIP_CLASS = {
  'Most Interested': 'bg-green-100 text-green-700',
  'Interested': 'bg-yellow-100 text-yellow-700',
  'Not Interested': 'bg-red-100 text-red-700',
  'Call Back': 'bg-blue-100 text-blue-700',
};
const NEUTRAL_CHIP_CLASS = 'bg-gray-100 text-gray-600';

// Shared focus ring classes for form fields
const FIELD_FOCUS = 'focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]';

const MostInterestedStudents = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());

  const reasons = [
    "Wrong Number",
    "Not Reachable",
    "Number Not Working",
    "Call Declined",
    "Busy"
  ];
  const [selectedRange, setSelectedRange] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('filter')?.toLowerCase() === 'today' ? 'today' : '';
  });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);

  const [formData, setFormData] = useState({
    student_name: '',
    gender: 'Male',
    father_name: '',
    mobile_number1: '',
    mobile_number2: '',
    email: '',
    aadhar_number: '',
    state: '',
    district: '',
    city: '',
    address: '',
    pincode: '',
    course: '',
    clg_name: '',
    program_type: '',
    ug_pg_course: '',
    call_back_reason: '',
    call_duration: '00:00:00.000000',
    other_reasons: '',
    is_interested: '',
    call_status: '',
    remarks: '',
    prev_call_back_reason: ''
  });

  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);

  const districts = [
    'Alluri Sitharama Raju', 'Anakapalli', 'Ananthapuram', 'Annamayya', 'Bapatla', 'CHITTOOR',
    'EAST GODAVARI', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema', 'Krishna', 'Kurnool',
    'Manyam', 'Nandyal', 'NTR', 'Palnadu', 'Prakasam', 'Srikakulam', 'SPS Nellore',
    'Sri Satyasai', 'Srikakulam', 'Tirupathi', 'Vizianagaram', 'Visakhapatnam',
    'West Godavari', 'YSR Kadapa'
  ];

  // search modal
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCollege, setSearchCollege] = useState('');

  // Lead closing state
  const [leadCloseModalIsOpen, setLeadCloseModalIsOpen] = useState(false);
  const [leadCloseDescription, setLeadCloseDescription] = useState('');
  const [studentToClose, setStudentToClose] = useState(null);
  const [loadingClose, setLoadingClose] = useState(false);
  const [closedLeads, setClosedLeads] = useState([]);
  const [viewClosedModalIsOpen, setViewClosedModalIsOpen] = useState(false);
  const [selectedClosedLeadData, setSelectedClosedLeadData] = useState(null);

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
      const memberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(memberType);

      let response;

      if (memberType === 'Admin' || memberType === 'Manager') {
        response = await fetch(SummaryApi.getAllMostInterestedStudents.url, {
          method: SummaryApi.getAllMostInterestedStudents.method,
          headers: {
            'Authorization': 'Bearer sandeep_university_916',
            'Content-Type': 'application/json',
          },
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) {
          throw new Error('Employee ID not found in session storage');
        }
        const decodedEmployeeId = atob(encodedEmployeeId);

        response = await fetch(SummaryApi.mostInterestedStudents.url, {
          method: SummaryApi.mostInterestedStudents.method,
          headers: {
            'Authorization': 'Bearer sandeep_university_916',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to fetch data from the endpoint');
      }

      const data = await response.json();

      const students = data?.data || []

      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
      }));
      setStudentsData(updatedStudentsData || []);
      setFilteredStudents(updatedStudentsData || []);
    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedLeads = async () => {
    try {
      const response = await fetch(SummaryApi.getClosingLead.url, {
        method: SummaryApi.getClosingLead.method,
        headers: headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status) {
          setClosedLeads(data.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching closed leads:", error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchClosedLeads();
  }, []);

  const closedLeadsMap = useMemo(() => {
    const map = {};
    closedLeads.forEach(lead => {
      map[String(lead.student_id)] = lead;
    });
    return map;
  }, [closedLeads]);

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    filterStudents(filter);
  };

  const filterStudents = (filter, search, range) => {
    let filtered = studentsData;

    const timeZone = 'Asia/Kolkata';

    const formatToKolkata = (date) => {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    if (filter !== 'All') {
      if (filter == "JEE Mains") {
        filtered = filtered.filter((student) => student.is_jee === "Jee Mains Results");
      } else {
        filtered = filtered.filter((student) => student.is_jee !== "Jee Mains Results");
      }
      if (filter == "JEE Mains2") {
        filtered = filtered.filter((student) => student.is_jee2 === "Jee Mains-2 Results");
      } else {
        filtered = filtered.filter((student) => student.is_jee2 !== "Jee Mains-2 Results");
      }
    }

    const convertToKolkataDateString = (date) => formatToKolkata(new Date(date));

    if (range === 'today') {
      const today = new Date();
      const todayFormatted = formatToKolkata(today);

      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === todayFormatted;
      });
    } else if (range === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);

      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === yesterdayFormatted;
      });
    } else if (range === 'thisWeek') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfWeek && studentDate <= endOfWeek;
      });
    } else if (range === 'lastWeek') {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);

      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfLastWeek && studentDate <= endOfLastWeek;
      });
    } else if (range === 'custom') {
      if (!fromDate || !toDate) {
        return;
      }

      const fromFormatted = new Date(fromDate);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(toDate);
      toFormatted.setHours(23, 59, 59, 999);

      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= fromFormatted && studentDate <= toFormatted;
      });
    }

    if (['Admin', 'Manager'].includes(memberType) && search) {
      filtered = filtered.filter((student) =>
        student.student_name.toLowerCase().includes(search.toLowerCase()) ||
        student.employee_name.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1.toLowerCase().includes(search.toLowerCase())
      );
    } else if (search) {
      filtered = filtered.filter((student) =>
        student.student_name.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  };

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map(student => new Date(student.created_date));
      const minDate = new Date(Math.min(...dates));
      setMinEnrolledDate(minDate);
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  useEffect(() => {
    if (formData.call_duration) {
      const [timePart] = formData.call_duration.split('.');
      const [hours, minutes, seconds] = timePart.split(':').map(part => part.padStart(2, '0'));

      setFormData((prevData) => ({
        ...prevData,
        hours,
        minutes,
        seconds,
      }));
    }
  }, [formData.call_duration]);

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === 'custom');
    if (range != 'custom') {
      setFromDate(null);
      setToDate(null);
      filterStudents(selectedFilter, searchQuery, range)
    } else {
      setFilteredStudents([])
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      filterStudents(selectedFilter, searchQuery, 'custom');
    }
  }, [fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    filterStudents(selectedFilter, e.target.value, selectedRange);
  };

  const ugCourses = ['CSE', 'CSE(AI & ML)', 'CSE(AI & DS)', 'AERONAUTICAL ENGINERRING', 'ECE'];
  const pgCourses = [];

  const courses = formData.program_type === 'UG' ? ugCourses : formData.program_type === 'PG' ? pgCourses : [];

  const openModal = (student = null, mode = 'view') => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A") {
          sanitizedData[key] = "";
        }
      });
      return sanitizedData;
    };

    const payload = {
      employee_id: student?.employee_id,
      student_name: student?.student_name,
      gender: student?.gender,
      father_name: student?.father_name,
      mobile_number1: student?.mobile_number1,
      mobile_number2: student?.mobile_number2 || '',
      email: student?.email || '',
      aadhar_number: student?.aadhar_number || '',
      state: student?.state,
      district: student?.district,
      city: student?.city,
      address: student?.address,
      pincode: student?.pincode || '',
      course: student?.course,
      clg_name: student?.clg_name,
      inter_percentage: student?.inter_percentage,
      program_type: student?.program_type,
      ug_pg_course: student?.ug_pg_course,
      call_status: student?.call_status || '',
      call_duration: student?.call_duration || '00:00:00.000000',
      remarks: student?.remarks || '',
      is_interested: reasons.includes(student?.is_interested) ? "Others" : student?.is_interested,
      other_reasons: student?.is_interested,
      call_back_reason: student?.call_back_reason,
      prev_call_back_reason: student?.call_back_reason,
      is_jee: student?.is_jee,
      is_jee2: student?.is_jee2
    };

    const sanitizedStudent = sanitizeStudentData(payload);

    const [timePart] = sanitizedStudent.call_duration.split('.');
    const [hours, minutes, seconds] = timePart.split(':');

    setFormData({
      ...sanitizedStudent,
      hours: hours.padStart(2, '0'),
      minutes: minutes.padStart(2, '0'),
      seconds: seconds.padStart(2, '0'),
    });

    if (mode === 'edit') {
      setIsUpdate(true);
      setIsUpload(false);
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    } else if (mode === 'add') {
      setFormData({
        student_name: '',
        gender: 'Male',
        father_name: '',
        mobile_number1: '',
        mobile_number2: '',
        email: '',
        aadhar_number: '',
        state: '',
        district: '',
        city: '',
        address: '',
        pincode: '',
        course: '',
        clg_name: '',
        remarks: '',
        call_status: '',
        call_duration: '00:00:00.000000',
        program_type: '',
        ug_pg_course: '',
        is_interested: '',
        hours: '00',
        minutes: '00',
        seconds: '00'
      });
      setIsUpdate(false);
      setIsUpload(true);
    } else {
      const coursesArray = sanitizedStudent.ug_pg_course
        ? sanitizedStudent.ug_pg_course.split(',').map(course => course.trim())
        : [];
      setSelectedCourses(coursesArray);
    }

    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
    setIsUpdate(false);
    setIsUpload(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'call_status' && (value === 'Not Answered' || value === '')) {
      setFormData((prev) => ({
        ...prev,
        is_interested: '',
        [name]: value,
      }));
    }
    else if (name === 'is_interested') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(value !== 'Call Back' && { call_back_reason: '' }),
        ...(value !== 'Others' && { other_reasons: '' }),
      }));
    } else if (name === 'program_type' && (value === 'PG' || value === '')) {
      setSelectedCourses([]);
      setDropdownValue('');
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedCourse = e.target.value;
    if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
      setSelectedCourses((prev) => [...prev, selectedCourse]);
    }
    setDropdownValue('');
  };

  const handleRemoveTag = (course) => {
    setSelectedCourses((prev) => prev.filter(c => c !== course));
  };

  const openLeadCloseModal = (student) => {
    setStudentToClose(student);
    setLeadCloseDescription('');
    setLeadCloseModalIsOpen(true);
  };

  const closeLeadCloseModal = () => {
    setLeadCloseModalIsOpen(false);
    setStudentToClose(null);
    setLeadCloseDescription('');
  };

  const handleLeadCloseSubmit = async (e) => {
    e.preventDefault();

    if (!leadCloseDescription.trim()) {
      toast.error("Please enter a description.");
      return;
    }

    setLoadingClose(true);

    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) throw new Error('Employee ID not found in session storage');
      const decodedEmployeeId = atob(encodedEmployeeId);

      const payload = {
        student_id: studentToClose.student_id,
        employee_id: decodedEmployeeId,
        description: leadCloseDescription
      };

      const response = await fetch(SummaryApi.leadClosing.url, {
        method: SummaryApi.leadClosing.method,
        headers: headers,
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.status) {
        toast.success("Lead closed successfully!");
        closeLeadCloseModal();
        fetchStudents();
      } else {
        toast.error(result.message || "Failed to close lead.");
      }

    } catch (error) {
      console.error("Lead Close Error:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingClose(false);
    }
  };

  const openViewClosedModal = (closedData) => {
    setSelectedClosedLeadData(closedData);
    setViewClosedModalIsOpen(true);
  };

  const closeViewClosedModal = () => {
    setViewClosedModalIsOpen(false);
    setSelectedClosedLeadData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) {
        throw new Error('Employee ID not found in session storage');
      }
      const decodedEmployeeId = atob(encodedEmployeeId);

      const apiEndpoint = isUpdate ? SummaryApi.updateStudent.url : SummaryApi.uploadStudent.url;
      const method = isUpdate ? SummaryApi.updateStudent.method : SummaryApi.uploadStudent.method;

      const payloadData = {
        employee_student_id: formData.employee_student_id,
        student_name: formData.student_name,
        last_name: '',
        gender: formData.gender,
        father_name: formData.father_name,
        mobile_number1: formData.mobile_number1,
        mobile_number2: formData.mobile_number2 || '',
        email: formData.email || '',
        aadhar_number: formData.aadhar_number || '',
        state: formData.state,
        district: formData.district,
        city: formData.city,
        address: formData.address,
        pincode: formData.pincode || '',
        course: formData.course,
        clg_name: formData.clg_name,
        inter_percentage: formData.inter_percentage || '',
        program_type: formData.program_type,
        ug_pg_course: selectedCourses.join(', '),
        call_status: formData.call_status || '',
        call_duration: formData.call_duration,
        remarks: formData.remarks || '',
        is_interested: formData.is_interested === 'Others' ? formData.other_reasons : formData.is_interested,
        call_back_reason: formData.call_back_reason || '',
        is_jee: (formData.is_jee === "Jee Mains Results")
          ? "Jee Mains Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains Results"
              ? "Jee Mains Results"
              : "")
            : "",

        is_jee2: (formData.is_jee2 === "Jee Mains-2 Results")
          ? "Jee Mains-2 Results"
          : (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested))
            ? (formData.prev_call_back_reason === "Jee Mains-2 Results"
              ? "Jee Mains-2 Results"
              : "")
            : "",
      };

      const payload = isUpdate
        ? {
          ...payloadData,
          student_id: selectedStudent.student_id,
          employee_id: decodedEmployeeId,
        }
        : {
          ...payloadData,
          employee_id: decodedEmployeeId,
        };


      const response = await fetch(apiEndpoint, {
        method: method,
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (result.status) {
        toast.success(isUpdate ? 'Student updated successfully!' : 'Student added successfully!');

        if (!isUpdate) {
          setEnrollmentCode(result.ENR_CODE);
        }

        closeModal();
        fetchStudents();
        setSelectedFilter('All')
        setSelectedCourses([])
        setDropdownValue('')
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleChangeCallDuration = (e) => {
    const value = Math.max(0, Math.min(e.target.value, 30));
    setFormData({
      ...formData,
      call_duration: value,
    });
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      'enrollment_code', 'student_name', 'email', 'mobile_number1', 'mobile_number2',
      'aadhar_number', 'state', 'district', 'city', 'address', 'pincode', 'course', 'clg_name',
      'created_date', 'call_status', 'call_duration', 'remarks', 'employee_name'
    ];

    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => {
        selectedData[field] = student[field];
      });
      return selectedData;
    });

    const headers = {
      enrollment_code: 'Enrollment Code',
      student_name: 'Student Name',
      email: 'Email',
      mobile_number1: 'Mobile Number 1',
      mobile_number2: 'Mobile Number 2',
      aadhar_number: 'Aadhar Number',
      state: 'State',
      district: 'District',
      city: 'City',
      address: 'Address',
      pincode: 'Pincode',
      course: 'Course',
      clg_name: 'College',
      created_date: 'Enrolled Date',
      call_status: 'Call Status',
      call_duration: 'Call Duration',
      remarks: 'Remarks',
      employee_name: 'Employee Name'
    };

    const formattedData = filteredData.map(student => {
      return Object.fromEntries(
        Object.entries(student).map(([key, value]) => [headers[key], value])
      );
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

    XLSX.writeFile(workbook, 'students_report.xlsx');
  };

  const openWhatsAppMessage = (studentData) => {
    const message = `🌟 Hello ${studentData.first_name} ${studentData.last_name}!.%0A
        We're thrilled to welcome you to GHRAISONI UNIVERSITY.%0A
        Your enrollment code is ${enrollmentCode}, and your mobile number is ${studentData.mobile_number1}.%0A
        You’ve chosen the ${studentData.course} course under the ${studentData.program_type} program.%0A
        We're excited about your journey with us, and if you have any questions, don’t hesitate to reach out.%0A
        Looking forward to seeing you soon! 🚀%0A
        Best Regards,%0A
        GHRAISONI UNIVERSITY.`;
    const phoneNumber = studentData.mobile_number1;
    const whatsappURL = `https://api.whatsapp.com/send?phone=91${phoneNumber}&text=${message}`;

    window.open(whatsappURL, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date)) {
      return "NA";
    }

    const options = { day: '2-digit', month: 'short', year: 'numeric' };

    return date.toLocaleDateString('en-GB', options);
  };

  // ────────────────────────────────────────────────
  // Table columns — Edit / View More / Lead Closing use action colors
  // and are marked sticky via STICKY_ACCESSORS
  // ────────────────────────────────────────────────
  const columns = React.useMemo(
    () => [
      {
        Header: 'SNO',
        Cell: ({ row }) => row.index + 1,
      },
      ...(['Admin', 'Manager'].includes(memberType) ? [
        {
          Header: 'Employee Name',
          accessor: 'employee_name',
        },
      ] : []),
      { Header: 'Student Name', accessor: 'student_name' },
      { Header: 'District', accessor: 'district' },
      { Header: 'City', accessor: 'city' },
      { Header: 'College Name', accessor: 'clg_name' },
      {
        Header: "Address",
        accessor: "address",
        Cell: ({ value }) => {
          if (!value || value === "N/A" || value === "n/a") {
            return <span className="text-gray-400">NA</span>;
          }
          return (
            <div className="group relative max-w-[100px]">
              <span className="block truncate text-gray-700">{value}</span>
              <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 left-0 top-6 shadow-lg w-max max-w-[300px] whitespace-normal break-words pointer-events-none">
                {value}
              </div>
            </div>
          );
        },
      },
      { Header: 'Mobile Number', accessor: 'mobile_number1' },
      {
        Header: 'Call Duration  (HH:MM:SS)',
        accessor: 'call_duration',
        Cell: ({ value }) => {
          if (value === "n/a") {
            return value;
          }
          if (value.includes(':')) {
            const [timePart] = value.split('.');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
          }
          return value;
        },
      },
      { Header: 'Course', accessor: 'course' },
      {
        Header: 'Enrolled Date',
        accessor: 'enroll_date',
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: 'Follow Up Date',
        accessor: 'follow_up_date',
        Cell: ({ row }) => {
          const { follow_up_date } = row.original;
          return <span>{formatDate(follow_up_date)}</span>;
        },
      },
      {
        Header: 'Previous Status',
        accessor: 'prevs_is_interested',
        Cell: ({ value }) => {
          if (!value) {
            value = 'NA';
          }
          const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;

          return (
            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>
              {value}
            </span>
          );
        },
      },
      {
        Header: 'Current Status',
        accessor: 'is_interested',
        Cell: ({ value }) => {
          const chipClass = STATUS_CHIP_CLASS[value] || NEUTRAL_CHIP_CLASS;

          return (
            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>
              {value}
            </span>
          );
        },
      },
      { Header: 'Jee Mains-1', accessor: 'is_jee' },
      { Header: 'Jee Mains-2', accessor: 'is_jee2' },
      ...(['Employee'].includes(memberType) ? [{
        Header: 'Edit',
        accessor: 'edit',
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionBlue }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
            className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            onClick={() => openModal(row.original, 'edit')}
          >
            <AiOutlineEdit className="shrink-0" />
            Edit
          </button>
        ),
      }] : []),
      {
        Header: 'View More',
        accessor: 'view',
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionYellow }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellowHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellow)}
            className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            onClick={() => openModal(row.original, 'view')}
          >
            <AiOutlineEye className="shrink-0" />
            View More
          </button>
        ),
      },
      {
        Header: 'Lead Closing',
        accessor: 'close',
        Cell: ({ row }) => {
          const studentId = String(row.original.student_id);
          const closedData = closedLeadsMap[studentId];

          if (closedData) {
            return (
              <button
                style={{ backgroundColor: THEME.actionPurple }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionPurpleHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionPurple)}
                className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                onClick={() => openViewClosedModal(closedData)}
              >
                <FaCheckCircle className="shrink-0" />
                Closed (View)
              </button>
            );
          } else {
            return (
              <button
                style={{ backgroundColor: THEME.actionGreen }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                onClick={() => openLeadCloseModal(row.original)}
              >
                <GrStatusGood className="shrink-0" />
                Lead Closing
              </button>
            );
          }
        },
      },
    ],
    [memberType, closedLeadsMap]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex, pageSize },
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
  } = useTable({
    columns,
    data: filteredStudents,
    initialState: {
      pageIndex: 0,
      pageSize: 100,
    },
  }, usePagination);

  // Sticky cell width map (must match render widths)
  const getStickyWidth = (accessor) => {
    if (accessor === 'edit') return 110;
    if (accessor === 'view') return 150;
    if (accessor === 'close') return 160;
    return 150;
  };

  // Row background based on state — sticky cells use this to match
  const getRowBg = (student) => {
    if (!student) return '#FFFFFF';
    return '#FFFFFF';
  };

  const clearFilters = () => {
    setSelectedRange('')
    setFromDate(null);
    setToDate(null);
    setIsCustomRange(false)
    setFilteredStudents(studentsData)
    setSearchQuery('')
    const url = new URL(window.location.href);
    url.searchParams.delete('filter');
    window.history.replaceState({}, '', url);
  };

  // Shared classes for pagination nav buttons
  const pageBtnClass =
    "px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

  // Shared classes for the student form fields (view mode = read-only look)
  const inputClass = `p-2 bg-slate-100 border border-gray-300 rounded-lg ${FIELD_FOCUS} ${!(isUpdate || isUpload) ? 'cursor-not-allowed' : ''}`;

  return (
    <div className="min-h-screen w-full">
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2"
            style={{ borderColor: THEME.primary }}
          />
        </div>
      ) : (
        <>
          <div className="w-full space-y-5">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-xl shadow-sm px-6 py-4 flex items-center gap-3">
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                    style={{ borderColor: THEME.primary }}
                  />
                  <span className="text-sm font-semibold" style={{ color: THEME.primary }}>
                    Submitting...
                  </span>
                </div>
              </div>
            )}

            <div className='flex justify-between items-center'>
              <h1
                className="text-2xl md:text-3xl font-bold"
                style={{ color: THEME.primary }}
              >
                Most Interested Students
              </h1>
              <div>
                <button
                  onClick={() => {
                    setSearchModalIsOpen(true);
                    setSearchDistrict('');
                    setSearchCity('');
                    setSearchCollege('');
                  }}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="text-white px-4 py-2 rounded-lg ml-4 flex items-center shadow-sm transition-opacity"
                >
                  Advanced Search
                </button>
              </div>
            </div>

            <div className='flex flex-wrap justify-between items-center gap-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4'>
              <div className="inline-flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'All' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'All' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('All')}
                >
                  All
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'JEE Mains' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'JEE Mains' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('JEE Mains')}
                >
                  JEE Mains-1
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'JEE Mains2' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'JEE Mains2' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('JEE Mains2')}
                >
                  JEE Mains-2
                </button>
                <button
                  className={`py-2 px-4 rounded-lg text-sm font-semibold transition ${selectedFilter === 'Not JEE Mains' ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  style={selectedFilter === 'Not JEE Mains' ? { backgroundColor: THEME.primary } : {}}
                  onClick={() => handleFilterChange('Not JEE Mains')}
                >
                  Non-JEE Mains
                </button>
              </div>

              {['Admin', 'Manager'].includes(memberType) && (
                <div>
                  <div className="flex items-center">
                    <label
                      htmlFor="dateRange"
                      style={{ color: THEME.primary }}
                      className="text-sm font-semibold"
                    >
                      Select Date Range:
                    </label>
                    <select
                      id="dateRange"
                      style={{ color: THEME.primary }}
                      className='py-2 px-4 ml-4 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                      onChange={handleRangeChange}
                      value={selectedRange}
                    >
                      <option value="">Select a range</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="thisWeek">This Week</option>
                      <option value="lastWeek">Last Week</option>
                      <option value="custom">Custom</option>
                    </select>

                    <button
                      onClick={clearFilters}
                      style={{ backgroundColor: THEME.actionRed }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                      className="px-4 py-2 ml-4 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors focus:outline-none"
                    >
                      Clear Filters
                    </button>
                  </div>

                  {isCustomRange && (
                    <div className='flex mt-4 gap-4'>
                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>From:</label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={minEnrolledDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select From Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]'
                        />
                      </div>

                      <div>
                        <label className="text-sm font-semibold" style={{ color: THEME.primary }}>To:</label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate || minEnrolledDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select To Date"
                          className='p-2 ml-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] disabled:bg-gray-100'
                          disabled={!fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='w-full md:w-1/3'>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={['Admin', 'Manager'].includes(memberType) ? "Search by student name or mobile number or employee name" : "Search by student name or mobile number"}
                  className="py-2 px-4 border border-gray-300 rounded-lg w-full text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                />
              </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <table {...getTableProps()} className="min-w-full mb-4 rounded-lg overflow-hidden text-sm text-gray-700">
                <thead className="bg-gray-200">
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => {
                        const isSticky = STICKY_ACCESSORS.includes(column.id) ||
                          STICKY_ACCESSORS.includes(column.accessor);
                        return (
                          <th
                            {...column.getHeaderProps()}
                            className={`px-4 py-2 text-left font-semibold whitespace-nowrap ${isSticky ? 'sticky right-0 z-20 bg-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]' : ''}`}
                            style={{ color: THEME.primary }}
                          >
                            {column.render('Header')}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map(row => {
                    prepareRow(row);
                    const student = row.original;
                    const stickyBg = getRowBg(student);

                    const stickyColsInOrder = row.cells.filter(cell =>
                      STICKY_ACCESSORS.includes(cell.column.id) ||
                      STICKY_ACCESSORS.includes(cell.column.accessor)
                    );

                    return (
                      <tr {...row.getRowProps()} className="hover:bg-slate-50 transition">
                        {row.cells.map(cell => {
                          const isSticky =
                            STICKY_ACCESSORS.includes(cell.column.id) ||
                            STICKY_ACCESSORS.includes(cell.column.accessor);

                          if (!isSticky) {
                            return (
                              <td {...cell.getCellProps()} className="border border-slate-100 px-4 py-2">
                                {cell.render('Cell')}
                              </td>
                            );
                          }

                          const stickyIndexInRow = stickyColsInOrder.indexOf(cell);
                          const totalSticky = stickyColsInOrder.length;
                          const stickyWidths = stickyColsInOrder.map(c =>
                            getStickyWidth(c.column.accessor || c.column.id)
                          );
                          let rightOffset = 0;
                          for (let i = stickyIndexInRow + 1; i < totalSticky; i++) {
                            rightOffset += stickyWidths[i];
                          }

                          return (
                            <td
                              {...cell.getCellProps()}
                              className="border border-slate-100 px-4 py-2 sticky z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]"
                              style={{
                                right: `${rightOffset}px`,
                                backgroundColor: stickyBg,
                                minWidth: `${stickyWidths[stickyIndexInRow]}px`,
                                width: `${stickyWidths[stickyIndexInRow]}px`,
                              }}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex justify-between items-center">
                <span style={{ color: THEME.primary }} className="text-sm font-medium">
                  Total records:{' '}
                  <span className="font-semibold">{filteredStudents.length}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    style={canPreviousPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {"<<"}
                  </button>
                  <button
                    onClick={previousPage}
                    disabled={!canPreviousPage}
                    style={canPreviousPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {"<"}
                  </button>
                  <span
                    className="px-3 py-1 mx-1 rounded-lg text-white text-sm font-semibold"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    Page {pageIndex + 1} of {pageCount}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={!canNextPage}
                    style={canNextPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {">"}
                  </button>
                  <button
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                    style={canNextPage ? { color: THEME.primary } : {}}
                    className={pageBtnClass}
                  >
                    {">>"}
                  </button>
                </div>
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  style={{ color: THEME.primary }}
                  className="ml-4 px-2 py-1 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                >
                  {[100, 200, 500, 1000].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Edit / View Student Modal */}
            <Modal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              contentLabel="Student Details"
              className="mx-auto my-10 mt-[100px] max-h-[85vh] max-w-full sm:max-w-4xl bg-white rounded-lg shadow-lg overflow-auto"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
            >
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
                <div className="flex justify-between items-center">
                  <h2
                    className="text-2xl font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    {isUpdate ? 'Edit Student' : isUpload ? 'Add Student' : 'Student Details'}
                  </h2>
                  <button onClick={closeModal}>
                    <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800 transition duration-200" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 sm:p-6">
                <div className="flex flex-col">
                  <label
                    htmlFor="student_name"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Student Name<span className='ml-1 text-red-600'>*</span>
                  </label>
                  <input
                    type="text"
                    id="student_name"
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="gender"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="father_name"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Father Name
                  </label>
                  <input
                    type="text"
                    id="father_name"
                    name="father_name"
                    value={formData.father_name}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="mobile_number1"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Mobile Number 1<span className='ml-1 text-red-600'>*</span>
                  </label>
                  <input
                    type="tel"
                    id="mobile_number1"
                    name="mobile_number1"
                    value={formData.mobile_number1}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setFormData((prev) => ({ ...prev, mobile_number1: value }));
                      }
                    }}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    required
                    pattern="\d{10}"
                    title="Mobile number must be exactly 10 digits."
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="mobile_number2"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Mobile Number 2:
                  </label>
                  <input
                    type="tel"
                    id="mobile_number2"
                    name="mobile_number2"
                    value={formData.mobile_number2}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setFormData((prev) => ({ ...prev, mobile_number2: value }));
                      }
                    }}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    pattern="\d{10}"
                    title="Mobile number must be exactly 10 digits."
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="email"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Email:
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="aadhar_number"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Aadhar Number:
                  </label>
                  <input
                    type="text"
                    id="aadhar_number"
                    name="aadhar_number"
                    value={formData.aadhar_number}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/-/g, '');
                      const formattedValue = rawValue
                        .replace(/\D/g, '')
                        .replace(/(\d{4})(?=\d)/g, '$1-');

                      if (formattedValue.length <= 14) {
                        setFormData((prev) => ({ ...prev, aadhar_number: formattedValue }));
                      }
                    }}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    pattern="(\d{4}-\d{4}-\d{4})"
                    title="Aadhar number must be in the format 1234-5678-9012."
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="state"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="district"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    District
                  </label>
                  <select
                    id="district"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  >
                    <option value="">Select District</option>
                    {districts.map((district, index) => (
                      <option key={index} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="city"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    City:
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="clg_name"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Collge Name:
                  </label>
                  <input
                    type="text"
                    id="clg_name"
                    name="clg_name"
                    value={formData.clg_name}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="address"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Address:
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="pincode"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Pincode:
                  </label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 6) {
                        setFormData((prev) => ({ ...prev, pincode: value }));
                      }
                    }}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    pattern="\d{6}"
                    title="Pincode must be exactly 6 digits."
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="course"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Course
                  </label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="clg_name"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    College
                  </label>
                  <input
                    type="text"
                    id="clg_name"
                    name="clg_name"
                    value={formData.clg_name}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="inter_percentage"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Intermediate Percentage/CGPA
                  </label>
                  <input
                    type="text"
                    id="inter_percentage"
                    name="inter_percentage"
                    value={formData.inter_percentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^(\d+(\.\d*)?|\.\d+)?$/;
                      if (regex.test(value) || value === '') {
                        setFormData((prev) => ({ ...prev, inter_percentage: value }));
                      }
                    }}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="program_type"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Program Type
                  </label>
                  <select
                    id="program_type"
                    name="program_type"
                    value={formData.program_type}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  >
                    <option value="">Select Program Type</option>
                    <option value="UG">UG</option>
                    <option value="PG">PG</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="ug_pg_course"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    UG/PG Course
                  </label>

                  <select
                    id="ug_pg_course"
                    name="ug_pg_course"
                    value={dropdownValue}
                    onChange={handleSelectChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course} value={course}>{course}</option>
                    ))}
                  </select>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedCourses.map((course) => (
                      <div
                        key={course}
                        className="flex items-center rounded-full px-3 py-1 border"
                        style={{
                          backgroundColor: THEME.primaryLight,
                          borderColor: 'rgba(23, 44, 87, 0.2)',
                          color: THEME.primary,
                        }}
                      >
                        <span>{course}</span>
                        <button
                          type="button"
                          className="ml-2"
                          disabled={!(isUpdate || isUpload)}
                          onClick={() => handleRemoveTag(course)}
                        >
                          <IoIosCloseCircle
                            className='w-5 h-5'
                            style={{ color: THEME.primary }}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="call_duration"
                    className="flex items-center text-sm font-semibold mb-2"
                    style={{ color: THEME.primary }}
                  >
                    Call Duration
                  </label>
                  <div className="flex flex-row space-x-2">
                    <div className="relative">
                      <input
                        type="text"
                        id="hours"
                        name="hours"
                        value={formData.hours.padStart(2, '0')}
                        className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-lg w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                        disabled
                      />
                      <span className="absolute right-2 top-2 text-gray-500 font-bold">h</span>
                    </div>
                    <div className="flex items-center text-gray-500 font-bold">:</div>
                    <div className="relative">
                      <input
                        type="text"
                        id="minutes"
                        name="minutes"
                        value={formData.minutes.padStart(2, '0')}
                        className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-lg w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                        disabled
                      />
                      <span className="absolute right-2 top-2 text-gray-500 font-bold">m</span>
                    </div>

                    <div className="flex items-center text-gray-500 font-bold">:</div>

                    <div className="relative">
                      <input
                        type="text"
                        id="seconds"
                        name="seconds"
                        value={formData.seconds.padStart(2, '0')}
                        className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-lg w-16 text-center shadow-sm focus:outline-none cursor-not-allowed"
                        disabled
                      />
                      <span className="absolute right-2 top-2 text-gray-500 font-bold">s</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col col-span-1 sm:col-span-2">
                  <label
                    htmlFor="call_status"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Call Status<span className='ml-1 text-red-600'>*</span>
                  </label>
                  <select
                    id="call_status"
                    name="call_status"
                    value={formData.call_status}
                    onChange={handleChange}
                    className={`p-2 bg-slate-100 border border-gray-300 rounded-lg ${FIELD_FOCUS} ${!(isUpdate || isUpload || formData.call_status !== 'Contacted') ? 'cursor-not-allowed' : ''}`}
                    disabled={formData.call_status === 'Contacted' || !(isUpdate || isUpload)}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Not Answered">Not Answered</option>
                  </select>
                </div>

                <div className="flex flex-col col-span-1 sm:col-span-2">
                  <label
                    htmlFor="remarks"
                    className="flex items-center text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Remarks:
                  </label>
                  <textarea
                    id="remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!(isUpdate || isUpload)}
                    rows={2}
                  />
                </div>

                <div className="flex flex-col col-span-1 sm:col-span-2">
                  <div className="flex flex-wrap gap-y-2">
                    <label
                      className="flex items-center mr-4 text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      <input
                        type="radio"
                        name="is_interested"
                        value="Not Interested"
                        checked={formData.is_interested === 'Not Interested'}
                        onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === 'Not Answered' || formData.call_status === '' || !(isUpdate || isUpload)}
                      />
                      Not Interested
                    </label>
                    <label
                      className="flex items-center mr-4 text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      <input
                        type="radio"
                        name="is_interested"
                        value="Interested"
                        checked={formData.is_interested === 'Interested'}
                        onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === 'Not Answered' || formData.call_status === '' || !(isUpdate || isUpload)}
                      />
                      Interested
                    </label>
                    <label
                      className="flex items-center mr-4 text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      <input
                        type="radio"
                        name="is_interested"
                        value="Most Interested"
                        checked={formData.is_interested === 'Most Interested'}
                        onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === 'Not Answered' || formData.call_status === '' || !(isUpdate || isUpload)}
                      />
                      Most Interested
                    </label>
                    <label
                      className="flex items-center mr-4 text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      <input
                        type="radio"
                        name="is_interested"
                        value="Call Back"
                        checked={formData.is_interested === 'Call Back'}
                        onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === 'Not Answered' || formData.call_status === '' || !(isUpdate || isUpload)}
                      />
                      Call Back
                    </label>
                    <label
                      className="flex items-center mr-4 text-sm font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      <input
                        type="radio"
                        name="is_interested"
                        value="Others"
                        checked={formData.is_interested === 'Others'}
                        onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === 'Not Answered' || formData.call_status === '' || !(isUpdate || isUpload)}
                      />
                      Others
                    </label>
                  </div>

                  <div className="flex items-center mt-4">
                    {formData.is_interested === 'Call Back' && (
                      <div className="mt-2 flex items-center justify-center">
                        <label
                          htmlFor="call_back_reason"
                          className="block mb-2 text-sm font-semibold"
                          style={{ color: THEME.primary }}
                        >
                          Call Back Reason<span className='ml-1 text-red-600'>*</span>:
                        </label>
                        <select
                          id="call_back_reason"
                          name="call_back_reason"
                          value={formData.call_back_reason || ''}
                          onChange={handleChange}
                          className={`p-2 border ml-4 border-gray-300 rounded-lg ${FIELD_FOCUS}`}
                          required={formData.is_interested === 'Call Back'}
                        >
                          <option value="">Select Reason</option>
                          <option value="IPE Exams">IPE Exams</option>
                          <option value="Jee Mains Results">JEE Mains Results</option>
                          <option value="Jee Mains-2 Results">JEE Mains-2 Results</option>
                          <option value="Eamcet Results">EAMCET Results</option>
                          <option value="IPE Results">IPE Results</option>
                          <option value="Supply Exams">Supply Exams</option>
                          <option value="Supply Results">Supply Results</option>
                          <option value="EOD">EOD</option>
                        </select>
                      </div>
                    )}

                    {formData.is_interested === 'Others' && (
                      <div className="mt-2 flex items-center justify-center">
                        <label
                          htmlFor="other_reasons"
                          className="block mb-2 text-sm font-semibold"
                          style={{ color: THEME.primary }}
                        >
                          Other Reason<span className='ml-1 text-red-600'>*</span>:
                        </label>
                        <select
                          id="other_reasons"
                          name="other_reasons"
                          value={formData.other_reasons || ''}
                          onChange={handleChange}
                          className={`p-2 border ml-4 border-gray-300 rounded-lg ${FIELD_FOCUS}`}
                          required={formData.is_interested === 'Others'}
                        >
                          <option value="">Select Reason</option>
                          <option value="Wrong Number">Wrong Number</option>
                          <option value="Not Reachable">Not Reachable</option>
                          <option value="Number Not Working">Number Not Working</option>
                          <option value="Call Declined">Call Declined</option>
                          <option value="Busy">Busy</option>
                          <option value="Switched Off">Switched Off</option>
                          <option value="Incoming Calls Stopped">Incoming Calls Stopped</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {(isUpdate || isUpload) && (
                  <div className="flex flex-col col-span-1 sm:col-span-2">
                    <button
                      type="submit"
                      style={{ backgroundColor: loadingSubmit ? THEME.disabledGray : THEME.primary }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      className="w-full text-white px-4 py-2 rounded-lg shadow-sm transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={!(isUpdate || isUpload) || loadingSubmit}
                    >
                      {loadingSubmit ? 'Submitting...' : (isUpdate ? 'Update Student' : 'Add Student')}
                    </button>
                  </div>
                )}
              </form>
            </Modal>

            {/* Search Modal */}
            <Modal
              isOpen={searchModalIsOpen}
              onRequestClose={() => {
                setSearchModalIsOpen(false);
                setSearchDistrict('');
                setSearchCity('');
                setSearchCollege('');
              }}
              contentLabel="Search Filters"
              className="mx-auto my-10 mt-[100px] w-full max-w-md bg-white rounded-lg shadow-lg"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
            >
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
                <div className="flex justify-between items-center">
                  <h2
                    className="text-xl font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Search Filters
                  </h2>
                  <button onClick={() => setSearchModalIsOpen(false)}>
                    <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex flex-col">
                  <label
                    htmlFor="district-filter"
                    className="mb-1 font-semibold text-sm"
                    style={{ color: THEME.primary }}
                  >
                    District
                  </label>
                  <select
                    id="district-filter"
                    className={`p-2 border border-gray-300 rounded-lg ${FIELD_FOCUS}`}
                    value={searchDistrict}
                    onChange={(e) => {
                      setSearchDistrict(e.target.value);
                      setSearchCity('');
                      setSearchCollege('');
                    }}
                  >
                    <option value="">Select District</option>
                    {Array.from(new Set(filteredStudents.map(student => student.district)))
                      .filter(Boolean)
                      .map(district => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="city-filter"
                    className="mb-1 font-semibold text-sm"
                    style={{ color: THEME.primary }}
                  >
                    City
                  </label>
                  <select
                    id="city-filter"
                    className={`p-2 border border-gray-300 rounded-lg disabled:bg-gray-100 ${FIELD_FOCUS}`}
                    value={searchCity}
                    onChange={(e) => {
                      setSearchCity(e.target.value);
                      setSearchCollege('');
                    }}
                    disabled={!searchDistrict}
                  >
                    <option value="">{searchDistrict ? "Select City" : "First select District"}</option>
                    {Array.from(new Set(
                      filteredStudents
                        .filter(student =>
                          searchDistrict && student.district?.toLowerCase() === searchDistrict.toLowerCase()
                        )
                        .map(student => student.city)
                    ))
                      .filter(Boolean)
                      .map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="college-filter"
                    className="mb-1 font-semibold text-sm"
                    style={{ color: THEME.primary }}
                  >
                    College Name
                  </label>
                  <select
                    id="college-filter"
                    className={`p-2 border border-gray-300 rounded-lg disabled:bg-gray-100 ${FIELD_FOCUS}`}
                    value={searchCollege}
                    onChange={(e) => setSearchCollege(e.target.value)}
                    disabled={!searchCity}
                  >
                    <option value="">{searchCity ? "Select College" : "First select City"}</option>
                    {Array.from(new Set(
                      filteredStudents
                        .filter(student =>
                          searchCity && student.city?.toLowerCase() === searchCity.toLowerCase()
                        )
                        .map(student => student.clg_name)
                    ))
                      .filter(Boolean)
                      .map(college => (
                        <option key={college} value={college}>
                          {college}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => {
                      setSearchDistrict('');
                      setSearchCity('');
                      setSearchCollege('');
                      setFilteredStudents(studentsData);
                    }}
                    style={{ backgroundColor: THEME.actionRed }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                    className="px-4 py-2 mr-2 text-white rounded-lg shadow-sm transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => {
                      const filtered = studentsData.filter(student => {
                        const districtMatch = !searchDistrict ||
                          student.district?.toLowerCase().includes(searchDistrict.toLowerCase());
                        const cityMatch = !searchCity ||
                          student.city?.toLowerCase().includes(searchCity.toLowerCase());
                        const collegeMatch = !searchCollege ||
                          student.clg_name?.toLowerCase().includes(searchCollege.toLowerCase());

                        return districtMatch && cityMatch && collegeMatch;
                      });
                      setFilteredStudents(filtered);
                      setSearchModalIsOpen(false);
                    }}
                    style={{ backgroundColor: THEME.primary }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    className="px-4 py-2 text-white rounded-lg shadow-sm transition-opacity"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Modal>

            {/* Lead Closing Modal */}
            <Modal
              isOpen={leadCloseModalIsOpen}
              onRequestClose={closeLeadCloseModal}
              contentLabel="Close Lead"
              className="mx-auto my-10 mt-[100px] w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
            >
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h2
                  className="text-xl font-semibold"
                  style={{ color: THEME.primary }}
                >
                  Close Lead
                </h2>
                <button onClick={closeLeadCloseModal}>
                  <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-700 font-medium mb-2">
                    Closing Lead for:{' '}
                    <span className="font-bold" style={{ color: THEME.primary }}>
                      {studentToClose?.student_name}
                    </span>
                  </p>
                  <p className="text-sm text-gray-500 mb-4">Enrollment Code: {studentToClose?.enrollment_code}</p>
                </div>

                <form onSubmit={handleLeadCloseSubmit}>
                  <div className="flex flex-col mb-4">
                    <label
                      htmlFor="closeDescription"
                      className="font-semibold mb-2 text-sm"
                      style={{ color: THEME.primary }}
                    >
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="closeDescription"
                      rows="4"
                      className={`p-3 border border-gray-300 rounded-lg placeholder-gray-400 ${FIELD_FOCUS}`}
                      placeholder="Enter details about why the lead is being closed (e.g., Successful counselling, Fees paid, etc.)"
                      value={leadCloseDescription}
                      onChange={(e) => setLeadCloseDescription(e.target.value)}
                      required
                    ></textarea>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeLeadCloseModal}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition"
                      disabled={loadingClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{ backgroundColor: THEME.actionGreen }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                      className={`px-4 py-2 text-white rounded-lg shadow-sm transition-colors flex items-center ${loadingClose ? 'opacity-70 cursor-not-allowed' : ''}`}
                      disabled={loadingClose}
                    >
                      {loadingClose && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {loadingClose ? 'Closing...' : 'Confirm Lead Close'}
                    </button>
                  </div>
                </form>
              </div>
            </Modal>

            {/* View Closed Lead Modal */}
            <Modal
              isOpen={viewClosedModalIsOpen}
              onRequestClose={closeViewClosedModal}
              contentLabel="Closed Lead Details"
              className="mx-auto my-10 mt-[100px] w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
            >
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h2
                  className="text-xl font-semibold flex items-center"
                  style={{ color: THEME.primary }}
                >
                  <FaCheckCircle className="mr-2 text-green-600" /> Lead Closed
                </h2>
                <button onClick={closeViewClosedModal}>
                  <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
                </button>
              </div>

              {selectedClosedLeadData && (
                <div className="p-6 space-y-4">
                  <div className="border-b border-slate-100 pb-3">
                    <p
                      className="text-sm uppercase font-bold tracking-wider"
                      style={{ color: THEME.primary }}
                    >
                      Student Details
                    </p>
                    <p className="text-lg font-semibold text-gray-800">{selectedClosedLeadData.student_name}</p>
                    <p className="text-gray-600">Student ID: {selectedClosedLeadData.student_id}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p
                      className="text-sm uppercase font-bold tracking-wider mb-2"
                      style={{ color: THEME.primary }}
                    >
                      Closed By
                    </p>
                    <div className="flex items-center">
                      <div
                        className="h-10 w-10 rounded-full flex items-center justify-center font-bold mr-3"
                        style={{ backgroundColor: THEME.primaryLight, color: THEME.primary }}
                      >
                        {selectedClosedLeadData.full_name ? selectedClosedLeadData.full_name.charAt(0) : 'E'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{selectedClosedLeadData.full_name || 'Unknown Employee'}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p
                      className="text-sm uppercase font-bold tracking-wider mb-1"
                      style={{ color: THEME.primary }}
                    >
                      Closing Remarks
                    </p>
                    <p className="text-gray-700 bg-white border border-gray-300 p-3 rounded-lg italic">
                      "{selectedClosedLeadData.description}"
                    </p>
                  </div>

                  <div className="text-right pt-2">
                    <p className="text-xs text-gray-400">
                      Closed on: {formatDate(selectedClosedLeadData.created_date)}
                    </p>
                  </div>
                </div>
              )}
              <div className="px-6 py-3 bg-gray-50 flex justify-end">
                <button
                  onClick={closeViewClosedModal}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="px-4 py-2 text-white rounded-lg shadow-sm transition-opacity"
                >
                  Close
                </button>
              </div>
            </Modal>

          </div>
        </>
      )}
    </div>
  );
};

export default MostInterestedStudents;